import React, { useState, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import EditorModal from './components/EditorModal';
import SplitScreenLayout from './components/SplitScreenLayout';
import InvoiceForm from './components/InvoiceForm';
import BulkLineItemsGrid from './components/BulkLineItemsGrid';
import PDFViewer from './components/PDFViewer';
import ImageViewer from './components/ImageViewer';

const InvoiceEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrgId } = useAuth();
  const params = new URLSearchParams(location.search || '');
  const invoiceId = location.state?.invoiceId || params.get('id');
  const viewOnly = location.state?.viewOnly || (params.get('view') === '1') || false;

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [confidenceScores, setConfidenceScores] = useState({});
  const [initialInvoiceSnapshot, setInitialInvoiceSnapshot] = useState(null);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(40);

  // Fetch invoice data from Supabase
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!invoiceId || !user?.id) {
        navigate('/invoices');
        return;
      }

      setIsLoading(true);
      try {
        // Fetch invoice with document and raw data
        // Build query; only filter org_id when available to avoid eq.null 400s on initial tab load
        let query = supabase
          .from('parsed_invoices')
          .select(`
            *,
            documents!inner(
              file_name,
              storage_path,
              uploaded_at
            ),
            raw_extracted_data(data)
          `)
          .eq('id', invoiceId);
        if (currentOrgId) query = query.eq('org_id', currentOrgId);
        const { data: invoice, error: invoiceError } = await query.single();

        if (invoiceError) throw invoiceError;

        // Get signed URL for PDF
        let pdfUrl = null;
        if (invoice?.documents?.storage_path) {
          console.log('Fetching signed URL for:', invoice.documents.storage_path);
          const { data: urlData, error: urlError } = await supabase.storage
            .from('invoice-documents')
            .createSignedUrl(invoice.documents.storage_path, 3600); // 1 hour expiry

          if (urlError) {
            console.error('Error creating signed URL:', urlError);
          } else {
            pdfUrl = urlData?.signedUrl;
            console.log('Signed URL generated:', pdfUrl);
          }
        } else {
          console.warn('No storage path found for document');
        }

        // Parse line items from raw data (envelope-aware). Fallback to direct query if join missing
        let rawData = invoice?.raw_extracted_data?.data || null;
        if (!rawData) {
          try {
            const { data: rawDirect } = await supabase
              .from('raw_extracted_data')
              .select('data')
              .eq('invoice_id', invoiceId)
              .maybeSingle();
            if (rawDirect?.data) rawData = rawDirect.data;
          } catch (e) {
            // ignore; we'll default to empty
          }
        }
        rawData = rawData || {};
        const lineItems = (
          rawData?.current?.line_items ||
          rawData?.parsed_json?.line_items ||
          rawData?.line_items ||
          []
        );

        // Transform to component format
        const documentName = invoice.documents?.file_name || 'Invoice';
        const documentExtension = (invoice.documents?.file_ext || documentName?.split('.')?.pop() || '')?.toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff'];
        const documentViewer = imageExtensions.includes(documentExtension) ? 'image' : 'pdf';

        const toPence = (x) => Math.round((parseFloat(x) || 0) * 100);
        const detectBasisAndPrice = (li) => {
          const qty = parseFloat(li?.quantity || 0) || 0;
          const caseQtyRaw = parseFloat(li?.case_quantity);
          const caseQty = Number.isFinite(caseQtyRaw) && caseQtyRaw > 0 ? caseQtyRaw : 0;
          const unit = parseFloat(li?.unit_price || 0) || 0;
          const netReported = parseFloat(li?.net_amount || 0) || 0;
          const tol = 2; // pence

          if (!qty || (!unit && !netReported)) {
            return { basis: caseQty > 0 ? 'each' : 'each', unitEach: unit || 0 };
          }

          // Hypothesis deltas from provided unit
          const netEachFromUnit = qty * (caseQty > 0 ? caseQty : 1) * unit;
          const netCaseFromUnit = qty * unit;
          const dEach = Math.abs(toPence(netReported) - toPence(netEachFromUnit));
          const dCase = Math.abs(toPence(netReported) - toPence(netCaseFromUnit));

          // Candidate unit prices derived from reported net
          const denomEach = qty * (caseQty > 0 ? caseQty : 1);
          const denomCase = qty;
          const unitFromEach = denomEach > 0 ? (netReported / denomEach) : unit;
          const unitFromCase = denomCase > 0 ? (netReported / denomCase) : unit;

          // Choose basis by which hypothesis is closer (≤ tol pence)
          let basis = 'each';
          if (caseQty <= 0) {
            basis = 'each';
          } else if (dCase <= dEach - 0) {
            basis = 'case';
          } else {
            basis = 'each';
          }

          // Prefer a unit price that exactly reconciles to reported net within tolerance
          let unitEach = unit;
          if (basis === 'each') {
            unitEach = Number.isFinite(unitFromEach) ? unitFromEach : unit;
          } else {
            // basis case: still store per-each for editor toggle? We store as-is and let recalc use basis
            // For consistent display with chosen basis, keep `unitPrice` representing the chosen basis.
            unitEach = Number.isFinite(unitFromCase) && caseQty > 0 ? (unitFromCase / caseQty) : (caseQty > 0 ? unit / caseQty : unit);
          }

          return { basis, unitEach };
        };

        const transformedData = {
          id: invoice.id,
          filename: documentName,
          pdfUrl: pdfUrl || null,
          documentViewer,
          vendorName: invoice.vendor_name || '',
          vendorAddress: '', // Not stored in current schema
          vendorVatNumber: '', // Not stored in current schema
          invoiceNumber: invoice.invoice_number || '',
          invoiceDate: invoice.invoice_date || '',
          dueDate: invoice.due_date || '',
          currency: invoice.currency || 'GBP',
          vendorId: invoice.vendor_account_ref || '',
          reviewed: invoice.status === 'reviewed' || invoice.status === 'exported',
          lineItems: lineItems.map((item, index) => {
            const qtyCases = parseFloat(item?.quantity || 0) || 0;
            const caseQty = parseFloat(item?.case_quantity || 0);
            const unitsPerCase = Number.isFinite(caseQty) && caseQty > 0 ? caseQty : 1;
            const detect = detectBasisAndPrice(item);
            const priceBasis = detect?.basis || 'each';
            // Derive a consistent unit price for the chosen basis.
            // We store unitPrice as per-each; recalc will interpret based on priceBasis.
            const unitEach = Number.isFinite(detect?.unitEach) ? detect.unitEach : (parseFloat(item?.unit_price || 0) || 0);
            // Prefer AI-provided vat_rate/vat_amount when present
            const aiRate = typeof item?.vat_rate === 'number' ? item?.vat_rate : parseFloat(item?.vat_rate || 0) || 0;
            const vatRate = Number.isFinite(aiRate) ? aiRate : 0;
            // Recompute net using basis-aware formula
            const computedNet = priceBasis === 'case'
              ? (qtyCases * (unitEach * unitsPerCase)) // unitEach is per each; convert to per-case for math
              : (qtyCases * unitsPerCase * unitEach);
            const net = (qtyCases > 0 && unitEach > 0)
              ? computedNet
              : (typeof item?.net_amount === 'number' ? item?.net_amount : computedNet);
            const computedVat = net * (vatRate / 100);
            // Always derive VAT from recomputed net to keep internal consistency
            const vatAmt = computedVat;
            const grossFromParts = net + vatAmt;
            const gross = (typeof item?.total === 'number' && Math.abs(item?.total - grossFromParts) < 0.01)
              ? item?.total
              : grossFromParts;
            const rateToCode = (rate) => {
              if (rate === 20) return 'S';
              if (rate === 5) return 'R';
              if (rate === 0) return 'Z';
              return 'O';
            };
            return {
              id: index + 1,
              description: item?.description || '',
              quantity: String(qtyCases || '1'),
              unitPrice: String(unitEach || '0'),
              caseQuantity: item?.case_quantity != null ? String(item?.case_quantity) : '',
              unitSize: item?.unit_size || '',
              priceBasis,
              vatRate: String(Number.isFinite(vatRate) ? vatRate : 0),
              vatCode: item?.vat_code || rateToCode(vatRate),
              netTotal: String(net?.toFixed ? net.toFixed(2) : net),
              vatAmount: String(vatAmt?.toFixed ? vatAmt.toFixed(2) : vatAmt),
              total: String(gross?.toFixed ? gross.toFixed(2) : gross)
            };
          }),
          comments: '',
          status: invoice.status,
          confidence: parseFloat(invoice.confidence || 0),
          createdAt: invoice.created_at,
          updatedAt: invoice.updated_at
          ,
          headerNet: parseFloat(invoice.net_amount || 0),
          headerVat: parseFloat(invoice.vat_amount || 0),
          headerGross: parseFloat(invoice.gross_amount || 0)
        };

        setInvoiceData(transformedData);
        setInitialInvoiceSnapshot(buildComparableSnapshot(transformedData));

        // Set confidence scores
        const cs = (
          rawData?.current?.confidence_scores ||
          rawData?.parsed_json?.confidence_scores ||
          rawData?.confidence_scores ||
          {}
        );
        setConfidenceScores({
          overall: parseFloat(invoice.confidence || 0),
          vendorName: parseFloat(cs?.vendor_name || invoice.confidence || 0),
          invoiceNumber: parseFloat(cs?.invoice_number || invoice.confidence || 0),
          invoiceDate: parseFloat(cs?.invoice_date || invoice.confidence || 0),
          lineItems: parseFloat(cs?.line_items || invoice.confidence || 0)
        });

      } catch (error) {
        console.error('Error fetching invoice:', error);
        alert('Failed to load invoice data');
        navigate('/invoices');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceData();
  }, [invoiceId, user?.id, currentOrgId, navigate]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate('/invoices');
  };

  const handleDataChange = (updatedData) => {
    setInvoiceData(updatedData);
    // Clear validation errors for changed fields
    setValidationErrors({});
  };

  const handleLineItemsChange = (items) => {
    const updated = { ...(invoiceData || {}), lineItems: items };
    handleDataChange(updated);
  };

  const handleOpenPdfAndResize = () => {
    const docUrl = invoiceData?.pdfUrl;
    if (!docUrl) {
      alert('No document URL available to open.');
      return;
    }
    const width = 1200;
    const height = 900;
    const left = Math.max(0, Math.round((window.screen.width - width) / 2));
    const top = Math.max(0, Math.round((window.screen.height - height) / 2));
    const features = [
      'noopener',
      'noreferrer',
      'popup',
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`
    ].join(',');
    window.open(docUrl, '_blank', features);
    setLeftPanelWidth(97);
  };

  function buildComparableSnapshot(data) {
    if (!data) return null;
    return {
      vendorName: data.vendorName || '',
      vendorAddress: data.vendorAddress || '',
      vendorVatNumber: data.vendorVatNumber || '',
      invoiceNumber: data.invoiceNumber || '',
      invoiceDate: data.invoiceDate || '',
      dueDate: data.dueDate || '',
      currency: data.currency || 'GBP',
      vendorId: data.vendorId || '',
      lineItems: (data.lineItems || []).map(item => ({
        description: item.description || '',
        quantity: item.quantity || '',
        unitPrice: item.unitPrice || '',
        vatRate: item.vatRate || '',
        vatCode: item.vatCode || '',
        netTotal: item.netTotal || '',
        vatAmount: item.vatAmount || '',
        total: item.total || ''
      }))
    };
  }

  const persistInvoiceChanges = async (opts = { requireVendor: false }) => {
    setIsLoading(true);
    try {
      // Validation
      const errors = {};
      if (!invoiceData?.vendorName?.trim()) {
        errors.vendorName = 'Vendor name is required';
      }
      if (!invoiceData?.invoiceNumber?.trim()) {
        errors.invoiceNumber = 'Invoice number is required';
      }
      if (opts?.requireVendor && !invoiceData?.vendorId) {
        errors.vendorId = 'Vendor selection is required';
      }

      if (Object.keys(errors)?.length > 0) {
        setValidationErrors(errors);
        // Throw to ensure caller does not show a success toast
        const firstMessage = Object.values(errors)?.find(Boolean) || 'Validation failed';
        const err = new Error(firstMessage);
        err.name = 'ValidationError';
        err.validationErrors = errors;
        throw err;
      }

      // Calculate amounts from line items (header totals)
      const netAmount = (invoiceData.lineItems || []).reduce(
        (sum, item) => sum + (parseFloat(item?.netTotal || 0) || 0),
        0
      );
      const vatAmount = (invoiceData.lineItems || []).reduce(
        (sum, item) => sum + (parseFloat(item?.vatAmount || 0) || 0),
        0
      );
      const grossAmount = netAmount + vatAmount;

      const originalSnapshot = initialInvoiceSnapshot;
      const currentSnapshot = buildComparableSnapshot(invoiceData);
      const manualChangesDetected =
        !!originalSnapshot &&
        !!currentSnapshot &&
        JSON.stringify(originalSnapshot) !== JSON.stringify(currentSnapshot);
      const manualEditMeta = manualChangesDetected
        ? {
            edited_by: user?.email || user?.id,
            edited_at: new Date().toISOString(),
            before: originalSnapshot,
            after: currentSnapshot
          }
        : null;

      // Update invoice in database
      const { error: updateError } = await supabase
        .from('parsed_invoices')
        .update({
          vendor_name: invoiceData.vendorName,
          invoice_number: invoiceData.invoiceNumber,
          invoice_date: invoiceData.invoiceDate || null,
          due_date: invoiceData.dueDate || null,
          currency: invoiceData.currency,
          vendor_account_ref: invoiceData.vendorId,
          net_amount: netAmount,
          vat_amount: vatAmount,
          gross_amount: grossAmount,
          confidence: manualChangesDetected ? 1 : invoiceData.confidence || 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (updateError) throw updateError;

      // Update raw_extracted_data envelope: preserve parsed_json/original_text, update only current
      const { data: rawRows, error: fetchRawErr } = await supabase
        .from('raw_extracted_data')
        .select('data')
        .eq('invoice_id', invoiceId);
      if (fetchRawErr) throw fetchRawErr;

      const prev = (rawRows && rawRows[0]?.data) || {};
      const prevCurrent = prev?.current || {};
        const updatedCurrent = {
          ...prevCurrent,
          line_items: invoiceData.lineItems.map(item => ({
            description: item.description,
            quantity: parseFloat(item.quantity || 0),
            unit_price: parseFloat(item.unitPrice || 0),
            case_quantity: item.caseQuantity !== '' && item.caseQuantity != null ? parseFloat(item.caseQuantity) : null,
            unit_size: item.unitSize || null,
            price_basis: item.priceBasis || null,
            net_amount: parseFloat(item.netTotal || 0),
            vat_rate: parseFloat(item.vatRate || 0),
            vat_amount: parseFloat(item.vatAmount || 0),
            vat_code: item.vatCode || null,
            total: parseFloat(item.total || 0)
          })),
        payment_terms: invoiceData.paymentTerms || null,
        confidence_scores: confidenceScores
      };

      const updatedEnvelope = {
        ...prev,
        current: updatedCurrent,
        // Back-compat: also reflect line_items at root for consumers expecting data.line_items
        line_items: updatedCurrent.line_items,
        manual_edit: manualChangesDetected ? true : prev?.manual_edit || false,
        manual_edit_meta: manualChangesDetected ? manualEditMeta : prev?.manual_edit_meta || null
      };

      const { error: rawError } = await supabase
        .from('raw_extracted_data')
        .upsert({ invoice_id: invoiceId, data: updatedEnvelope }, { onConflict: 'invoice_id' });

      if (rawError) throw rawError;

      if (manualChangesDetected) {
        await supabase
          .from('audit_log')
          .insert({
            entity_type: 'parsed_invoices',
            entity_id: invoiceId,
            action: 'manual_edit',
            user_id: user?.id,
            org_id: currentOrgId,
            meta_json: manualEditMeta
          });
      }

      if (manualChangesDetected) {
        setInitialInvoiceSnapshot(currentSnapshot);
      }

      return { manualChangesDetected };

    } catch (error) {
      console.error('Save failed:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await persistInvoiceChanges({ requireVendor: false });
      alert('Invoice saved successfully!');
      setIsModalOpen(false);
      setTimeout(() => navigate('/invoices'), 100);
    } catch (error) {
      if (error?.name === 'ValidationError') {
        alert('Please correct the highlighted fields before saving.');
      } else {
        alert('Failed to save invoice. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!invoiceData?.invoiceDate) {
      alert('Please supply an invoice date before marking this invoice as reviewed.');
      return;
    }

    setIsLoading(true);
    try {
      await persistInvoiceChanges({ requireVendor: true });

      const { error } = await supabase
        .from('parsed_invoices')
        .update({
          status: 'reviewed',
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (error) throw error;

      const updatedData = { ...invoiceData, reviewed: true, status: 'reviewed', confidence: 1 };
      setInvoiceData(updatedData);

      alert('Invoice reviewed and ready for export.');
      setIsModalOpen(false);
      setTimeout(() => navigate('/invoices'), 100);

    } catch (error) {
      console.error('Review update failed:', error);
      if (error?.name === 'ValidationError') {
        const messages = Object.values(error?.validationErrors || {})?.filter(Boolean);
        const humanMessage = messages?.[0] || 'Please correct the highlighted fields before marking as reviewed.';
        alert(humanMessage);
      } else {
        alert(error?.message || 'Failed to mark as reviewed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReprocess = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));

      const newConfidenceScores = {
        ...confidenceScores,
        overall: Math.min(0.95, confidenceScores?.overall + 0.05),
        vendorAddress: Math.min(0.95, confidenceScores?.vendorAddress + 0.07),
        dueDate: Math.min(0.95, confidenceScores?.dueDate + 0.15)
      };
      setConfidenceScores(newConfidenceScores);

      console.log('Invoice reprocessed with Mistral OCR');
    } catch (error) {
      console.error('Reprocessing failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePDFLoadSuccess = ({ numPages }) => {
    console.log('PDF loaded successfully:', numPages, 'pages');
  };

  const handlePDFLoadError = (error) => {
    console.error('PDF load error:', error);
  };

  if (!invoiceData && isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">Loading invoice editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <EditorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={`Invoice Editor - ${invoiceData?.invoiceNumber || 'Loading...'}`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex justify-end px-4 py-2 border-b border-border bg-muted/20">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenPdfAndResize}
                iconName="ExternalLink"
                iconPosition="left"
              >
                Open PDF and Resize
              </Button>
              <Button
                variant={isBulkEdit ? 'outline' : 'primary'}
                size="sm"
                onClick={() => setIsBulkEdit((prev) => !prev)}
                iconName={isBulkEdit ? 'LayoutPanelLeft' : 'Grid'}
                iconPosition="left"
              >
                {isBulkEdit ? 'Normal View' : 'Bulk Edit'}
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <SplitScreenLayout
              className="h-full"
              leftWidth={leftPanelWidth}
              onLeftWidthChange={setLeftPanelWidth}
              leftTitle={isBulkEdit ? 'Bulk Edit' : 'Invoice Form'}
              rightTitle="PDF Document"
              leftPanel={
                isBulkEdit ? (
                  <BulkLineItemsGrid
                    lineItems={invoiceData?.lineItems || []}
                    onChange={handleLineItemsChange}
                  />
                ) : (
                  <InvoiceForm
                    invoiceData={invoiceData}
                    onDataChange={handleDataChange}
                    onSave={handleSave}
                    onReviewed={handleMarkReviewed}
                    onReprocess={handleReprocess}
                    isLoading={isLoading}
                    validationErrors={validationErrors}
                    confidenceScores={confidenceScores}
                  />
                )
              }
              rightPanel={
                invoiceData?.documentViewer === 'image' ? (
                  <ImageViewer
                    imageUrl={invoiceData?.pdfUrl}
                    filename={invoiceData?.filename}
                  />
                ) : (
                  <PDFViewer
                    pdfUrl={invoiceData?.pdfUrl}
                    onLoadSuccess={handlePDFLoadSuccess}
                    onLoadError={handlePDFLoadError}
                  />
                )
              }
            />
          </div>
        </div>
      </EditorModal>
    </div>
  );
};

export default InvoiceEditor;
